import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import PublicNav from 'layouts/PublicNav';
import { LICENCE_ISSUER_URL } from 'config/licenceIssuer';
import './SelfHostedCheckout.css';

// The Installation ID shape the licence issuer accepts (8–64 letters, digits
// or dashes) — checked here only so a mistyped paste gets a friendly word
// before the round trip; the issuer is the authority.
const INSTALLATION_ID = /^[A-Za-z0-9-]{8,64}$/;

/**
 * Ask the licence issuer for the licence this Checkout paid for.
 * @returns {Promise<{ status: number, body: object }>}
 */
async function claimLicence(sessionId, boxId) {
  const res = await fetch(`${LICENCE_ISSUER_URL}/paid-licence/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(boxId ? { sessionId, boxId } : { sessionId }),
  });
  let body = {};
  try { body = await res.json(); } catch { /* an empty or non-JSON answer reads as a plain failure */ }
  return { status: res.status, body: body || {} };
}

const LicenceKey = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* the key stays selectable in the box below */ }
  };
  return (
    <div className="shc-key">
      <button type="button" className="shc-link-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide licence key' : 'Show licence key'}
      </button>
      {open && (
        <div className="shc-key-body">
          <p className="shc-text shc-text--muted shc-text--small">
            Only needed if your machine is offline: paste this into License in your dashboard.
          </p>
          <textarea className="shc-key-text" readOnly value={token} rows={4} aria-label="Licence key" onFocus={(e) => e.target.select()} />
          <div className="shc-actions shc-actions--left">
            <button type="button" className="shc-btn shc-btn--outline" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * /self-hosted/checkout/success?session_id=
 *
 * Stripe sends the buyer here after paying. The page claims the licence from
 * the issuer (public, no sign-in). If the purchase didn't name a machine, the
 * issuer asks which one — the buyer pastes the Installation ID and continues.
 */
const SelfHostedCheckoutSuccessPage = () => {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get('session_id') || '';
  // claiming | done | needsBox | error
  const [state, setState] = useState(sessionId ? 'claiming' : 'error');
  const [token, setToken] = useState('');
  const [boxId, setBoxId] = useState('');
  const [boxHint, setBoxHint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const started = useRef(false);

  const claim = useCallback(async (withBox) => {
    try {
      const { status, body } = await claimLicence(sessionId, withBox);
      if (status === 200 && body.token) {
        setToken(body.token);
        setState('done');
        return;
      }
      if (status === 409 && body.error === 'box_required') {
        if (withBox) setBoxHint('That installation wasn’t accepted. Copy the ID again from the License dialog and try once more.');
        setState('needsBox');
        return;
      }
      setState('error');
    } catch {
      setState('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (started.current || !sessionId) return;
    started.current = true;
    claim(null);
  }, [claim, sessionId]);

  const submitBox = async (e) => {
    e.preventDefault();
    const id = boxId.trim();
    if (!INSTALLATION_ID.test(id)) {
      setBoxHint('That doesn’t look like an Installation ID. In your dashboard, open License and press Copy installation ID.');
      return;
    }
    setBoxHint('');
    setSubmitting(true);
    await claim(id);
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Your licence — Teamy</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="public-page shc-page">
        <PublicNav />
        <main className="shc-main">
          <div className="shc-card" role="status" aria-live="polite">
            {state === 'claiming' && (
              <>
                <div className="shc-spinner" aria-hidden="true" />
                <h1 className="shc-title">Finishing your purchase…</h1>
              </>
            )}

            {state === 'done' && (
              <>
                <div className="shc-check" aria-hidden="true">✓</div>
                <h1 className="shc-title">You’re all set</h1>
                <p className="shc-text">
                  Your machine picks up the licence by itself — open License in your dashboard, or it updates within a day.
                </p>
                <LicenceKey token={token} />
              </>
            )}

            {state === 'needsBox' && (
              <>
                <h1 className="shc-title">Which machine is this for?</h1>
                <p className="shc-text">Your payment went through. Tell us where the licence goes and we’ll put it there.</p>
                <form className="shc-form" onSubmit={submitBox}>
                  <label className="shc-label" htmlFor="shc-box">
                    Installation ID <span className="shc-label-hint">(License dialog → Copy installation ID)</span>
                  </label>
                  <input
                    id="shc-box"
                    className="shc-input"
                    value={boxId}
                    onChange={(e) => setBoxId(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                  />
                  {boxHint && <p className="shc-hint" role="alert">{boxHint}</p>}
                  <div className="shc-actions">
                    <button type="submit" className="shc-btn shc-btn--solid" disabled={submitting || !boxId.trim()}>
                      {submitting ? 'One moment…' : 'Continue'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {state === 'error' && (
              <>
                <h1 className="shc-title">We couldn’t finish setting up your licence</h1>
                <p className="shc-text">
                  {sessionId
                    ? 'If you were charged, your payment is safe — we’ll sort it out with you.'
                    : 'This page needs the link from your checkout, and this one is incomplete.'}
                </p>
                <p className="shc-text">
                  Contact <a href="mailto:support@teamy.town">support@teamy.town</a>.
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default SelfHostedCheckoutSuccessPage;
